import axiosInstance from "../../../common/axiosInstance";
import axios from "axios";
import { Budget } from "../types/budgeting";
import { usePremisesStore } from "../../../store/store";

const handleApiError = (error: any): { message: string; status?: number } => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      if (error.response.status === 401) {
        return {
          message: "Authentication failed. Please log in again.",
          status: 401,
        };
      }
      if (error.response.status === 403) {
        return {
          message: "You do not have permission to access this tenant's data.",
          status: 403,
        };
      }
      return {
        message:
          error.response.data?.message ||
          "An error occurred while communicating with the server.",
        status: error.response.status,
      };
    }
    return {
      message: "Network error. Please check your connection and try again.",
    };
  }
  return { message: "An unexpected error occurred." };
};

export const fetchBudgetById = async (id: string) => {
try {
    const response = await axiosInstance.get(
      `/api/budgeting/${id}`
    );

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
export const fetchPremisesData = async () => {

  try {
    const response = await axiosInstance.get(`/api/premises`);
    return response.data.map((premise: any) => ({
      id: premise.id,
      premisesName: premise.premisesName,
      addressLine1: premise.addressLine1,
      townCity: premise.townCity,
      region: premise.region,
      postcode: premise.postcode,
    }));
  } catch (error) {
    throw handleApiError(error);
  }
};

export const fetchBudgetData = async (
  premisesId?: string,
  moduleName?: string,
  regionId?: string
) => {

  const params = new URLSearchParams();

  if (moduleName) {
    params.append("moduleType", moduleName);
  }

  if (regionId) {
    params.append("regionId", regionId);
  }

  if (premisesId) {
    params.append("premisesId", premisesId);
  }

  const response = await axiosInstance.get(
    `/api/budgeting?${params.toString()}`
  );

  return response.data;
};

export const fetchStatusCounts = async (moduleType?: string) => {

  let url = `/api/budgeting/status-counts`;
  if (moduleType) {
    url += `?moduleType=${encodeURIComponent(moduleType)}`;
  }

  try {
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateBudgetStatus = async (id: string, status: string) => {

  try {
    await axiosInstance.patch(
      `/api/budgeting/${id}/status`,
      { status }
    );
  } catch (error) {
    throw handleApiError(error);
  }
};

export const updateItemStatus = async (
  id: string,
  status: string,
  riskMitigationPlan?: string
) => {

  try {
    await axiosInstance.patch(
      `/api/item/${id}/status`,
      {
        approvalStatus: status,
        riskMitigationPlan: riskMitigationPlan,
      }
    );
  } catch (error) {
    throw handleApiError(error);
  }
};

export const saveBudget = async (
  budget: Budget,
  id?: string,
  isUpdate = false,
  moduleType?: string
) => {

  const formData = {
    premises: budget.premises,
    premisesAddress: budget.premisesAddress,
    requestedBy: budget.requestedBy || undefined,
    approvalManager: budget.approvalManager || undefined,
    status: budget.status || "Pending",
    moduleType: moduleType || "fire_safety",
    categories: budget.categories.map((category) => ({
      id: category.id,
      category: category.category,
      vendor: category.vendor.name
        ? {
          id: category.vendor.id,
          name: category.vendor.name,
          phoneNumber: category.vendor.phoneNumber,
          email: category.vendor.email,
          websiteAddress: category.vendor.websiteAddress,
        }
        : null,
      items: category.items.map((item) => ({
        id: item.id,
        name: item.name,
        numbersNeeded: parseFloat(item.numbersNeeded) || 0,
        expectedCostPerUnit: parseFloat(item.expectedCostPerUnit) || 0,
        totalCost: parseFloat(item.totalCost) || 0,
        description: item.description || "",
        justification: item.justification || "",
        approvalStatus: item.approvalStatus || "Pending",
        riskMitigationPlan: item.riskMitigationPlan || "",
      })),
      files: category.files?.filter((file) => typeof file === "string") || [],
    })),
    emailOptions: budget.emailOptions
      .filter((email) => email.email.trim() !== "")
      .map((email) => ({
        id: email.id,
        email: email.email,
      })),
  };

  // Remove undefined ids from new categories and items
  formData.categories = formData.categories
    .filter((category) => category.id !== undefined)
    .map((category) => ({
      ...category,
      vendor: category.vendor
        ? {
          ...category.vendor,
          id: category.vendor.id || undefined,
        }
        : null,
      items: category.items.map((item) => ({
        ...item,
        id: item.id || undefined,
      })),
    }));

  try {
    if (isUpdate && id) {
      return await axiosInstance.put(`/api/budgeting/${id}`, formData);
    } else {
      return await axiosInstance.post(`/api/budgeting`, formData);
    }
  } catch (error) {
    throw handleApiError(error);
  }
};

export const sendBudgetByEmail = async (budget: Budget) => {

  const validEmails = budget.emailOptions
    .filter((option) => option.email.trim() !== "")
    .map((option) => option.email);

  if (validEmails.length === 0) {
    throw new Error("No valid email addresses provided");
  }

  // Fetch premises name from store or API
  let premisesName = budget.premises;
  try {
    const premisesStore = usePremisesStore.getState();
    const premise = premisesStore.allPremises.find(
      (p) => p.id === budget.premises
    );
    premisesName = premise ? premise.premisesName : budget.premises;
  } catch (error) {
    console.error("Error fetching premises name:", error);
  }

  const budgetSummary = `
    Budget Request: ${premisesName}
    Address: ${budget.premisesAddress}
    Status: ${budget.status}
    
    Categories and Items:
    ${budget.categories
      .map(
        (category) => `
      Category: ${category.category}
      Vendor: ${category.vendor.name}
      Items: ${category.items
            .map(
              (item) => `
        - ${item.name}: ${item.numbersNeeded} units at $${item.expectedCostPerUnit} each = $${item.totalCost}
        - Description: ${item.description}
        - Status: ${item.approvalStatus}
      `
            )
            .join("")}
    `
      )
      .join("")}
  `;

  const emailData = {
    to: validEmails,
    cc: [],
    bcc: [],
    subject: `Budget Request for ${premisesName}`,
    body: budgetSummary,
    templateType: "generic",
    additionalVariables: [
      { key: "budgetId", value: budget.id || "" },
      { key: "premises", value: premisesName },
      { key: "status", value: budget.status || "Pending" },
    ],
  };

  try {
    return await axiosInstance.post(`/api/email`, emailData);
  } catch (error) {
    throw handleApiError(error);
  }
};
